/* eslint-disable import/no-anonymous-default-export */
/* eslint-disable max-len */
/**
 * wsui相关接口
 */
import http from '@utils/http';
import httpAction from './httpActionConfig';
import { SSEClient, SSEOptions } from '@utils/http/sse';
import baseUrl from '@utils/http/baseUrl';
import type { IGetRecommendSceneResponse } from '@typing/wsui/getRecommendScene';
import type { IGetMessagesResponse, IGetMessagesParams } from '@typing/wsui/getMessages';
import type { IGetSessionsResponse, IGetSessionsParams } from '@typing/wsui/getSessions';
import type { IUserMeResponse } from '@typing/wsui/userMe';
import type { IConversationParams } from '@typing/wsui/msg';
import type { IGetConfigResponse } from '@typing/wsui/getConfig';
import type {
    IDeleteSessionParams,
    IDeleteSessionResponse,
    IUpdateSessionParams,
    IUpdateSessionResponse,
} from '@typing/wsui/session';
import type { IBatchGetAgreementResponse, IAgreementSignItem, IAgreementSignResponse } from '@typing/wsui/agreement';
import type { IReferenceContentResponse, IReferenceContentParams } from '@typing/wsui/referenceContent';
import type { IHeguiVerifyParams, IHeguiVerifyData } from '@typing/wsui/hegui';
import type { ICoredataParams, ICoredataResponse } from '@typing/wsui/coredata';
import type { IOtherdataParams, IOtherdataResponse } from '@typing/wsui/otherdata';
import type { ICommonActionParams, ICommonActionResponse } from '@typing/wsui/commonaction';
import type { IPollingParams, IPollingResponse } from '@typing/wsui/polling';
import { getFkParamsAll } from '@utils/riskControler';
import type { FkParamsVal } from '@utils/riskControler/index.d';
import type { IGetBucketTokenParams, IGetBucketTokenResponse } from '@typing/wsui/getBucketToken';
import type { IPreConsultationDetail, IPreConsultationDetailParams } from '@typing/wsui/preConsultation';

const { post, get } = httpAction(http.wsuiAxios);

/**
 * 获取风控参数，失败时返回空值兜底
 */
async function fetchFkParams(ev: string): Promise<FkParamsVal> {
    try {
        return await getFkParamsAll({ ev });
    } catch (error) {
        console.error('获取风控参数失败:', error);
        return { jt: '', app: '', js_env: '', z: '' };
    }
}

export default {
    fetchFkParams,
    getConfig: (data: { businessType: string }) =>
        get<HttpResonseBase<IGetConfigResponse>>('/wsui/oe/advisor/getconfig'), // 获取对话配置
    getRecommendScene: (data: { businessType: string }) =>
        get<HttpResonseBase<IGetRecommendSceneResponse>>('/wsui/oe/advisor/getrecommendscene'), // 获取推荐场景
    getSessions: (data: IGetSessionsParams) =>
        post<HttpResonseBase<IGetSessionsResponse>>('/wsui/oe/doctorclaw/sessionlist', data), // 获取历史会话列表
    getReferenceContent: (data: IReferenceContentParams) =>
        post<HttpResonseBase<IReferenceContentResponse>>('/wsui/oe/advisor/referencecontent', data, {
            enableAcsToken: true,
        }), // 获取引用内容详情
    userMe: async (data: { businessType: string }) => {
        const fkParams = await fetchFkParams('page_enter');
        return get<HttpResonseBase<IUserMeResponse>>('/wsui/oe/advisor/me', { ...fkParams, ...data });
    }, // 获取当前用户信息

    /** 获取新 sessionId（页面入口调用一次） */
    getCoredata: (data?: ICoredataParams) =>
        post<HttpResonseBase<ICoredataResponse>>('/wsui/oe/doctorclaw/coredata', data || {}),

    /** 获取配置数据 */
    getOtherdata: (data: IOtherdataParams) =>
        post<HttpResonseBase<IOtherdataResponse>>('/wsui/oe/doctorclaw/otherdata', data),

    /** SSE 对话接口（doctorclaw/msg，同时供 OE 模式和融合模式使用） */
    conversation: (data: IConversationParams, options: SSEOptions) => {
        const prefix = baseUrl.wsui === '/' ? '' : baseUrl.wsui;
        const url = `${prefix}/wsui/oe/doctorclaw/msg`;
        const client = new SSEClient();
        client.connect(url, options, 'POST', data);
        return client;
    },
    deleteSession: (data: IDeleteSessionParams) =>
        post<HttpResonseBase<IDeleteSessionResponse>>('/wsui/oe/doctorclaw/sessiondelete', data), // 删除会话
    updateSession: (data: IUpdateSessionParams) =>
        post<HttpResonseBase<IUpdateSessionResponse>>('/wsui/oe/doctorclaw/sessionupdate', data), // 更新会话名称
    batchGetAgreement: (data: { businessType: string }) =>
        post<HttpResonseBase<IBatchGetAgreementResponse>>('/wsui/oe/advisor/common/batchgetagreement', {}), // 获取协议列表
    agreementSign: (data: { agreements: IAgreementSignItem[]; businessType: string }) =>
        post<HttpResonseBase<IAgreementSignResponse>>('/wsui/oe/advisor/common/agreementsign', data), // 签署协议
    heguiVerify: (data: IHeguiVerifyParams) =>
        post<HttpResonseBase<IHeguiVerifyData>>('/wsui/oe/advisor/hegui_verify', data), // 合规校验

    /** 获取历史消息列表（doctorclaw/messagelist） */
    getMessages: (data: IGetMessagesParams) =>
        post<HttpResonseBase<IGetMessagesResponse>>('/wsui/oe/doctorclaw/messagelist', data),
    /** 首次对话后开始轮询，直到页面关闭才停止 */
    polling: (data: IPollingParams) => post<HttpResonseBase<IPollingResponse>>('/wsui/oe/doctorclaw/polling', data),
    /** 点赞/点踩（替代原 like 接口） */
    commonAction: (data: ICommonActionParams) =>
        post<HttpResonseBase<ICommonActionResponse>>('/wsui/oe/doctorclaw/commonaction', data),
    /** 获取 BOS 上传 Token */
    getBucketToken: (data: IGetBucketTokenParams) =>
        post<HttpResonseBase<IGetBucketTokenResponse>>('/wsui/oe/doctorclaw/getBucketToken', data),
    /** 获取问诊前置详情 */
    getPreConsultationDetail: (data: IPreConsultationDetailParams) =>
        post<HttpResonseBase<IPreConsultationDetail>>('/wsui/h5/preconsult/detail', data),
};
